import { v4 as uuid } from 'uuid';
import { ACTIVITY_SEQUENCE_TYPE, ACTIVITY_TYPES } from 'constants/general';
import { ActivityImagesDataType, LogQuantityQuestion } from 'interfaces';
import { ActivityType, ChoiceType } from 'interfaces/common.interface';
import {
  SettingsType,
  UnlockHabitPackTarget
} from 'interfaces/setting.interface';
import { isNumber } from 'utils/number.util';

/**
 * TEMP: Habit pack templates may include R2 `file_path` values that are not valid
 * when copied into a user's routine settings. Strip those until the backend
 * returns user-scoped paths or public URLs on add-from-pack.
 * Remove once backend fixes pack → routine activity image handling.
 */
const stripFilePathsFromActivityImages = (
  images?: ActivityImagesDataType[]
): ActivityImagesDataType[] | undefined => {
  if (!images?.length) {
    return images;
  }

  const normalized = images
    .map(({ url, file_path: _filePath, ...rest }) => {
      const trimmedUrl = url?.trim();
      return trimmedUrl ? { ...rest, url: trimmedUrl } : null;
    })
    .filter((entry): entry is NonNullable<typeof entry> => entry !== null);

  return normalized.length ? normalized : undefined;
};

export const normalizeHabitPackActivityForRoutine = (
  activity: ActivityType
): ActivityType => ({
  ...activity,
  image_urls: stripFilePathsFromActivityImages(activity.image_urls)
});

export const PACK_ROUTINE_CATEGORY = {
  MORNING: 'morning',
  EVENING: 'evening',
  BREAK: 'break',
  STANDALONE: 'standalone'
} as const;

export type PackRoutineCategory =
  (typeof PACK_ROUTINE_CATEGORY)[keyof typeof PACK_ROUTINE_CATEGORY];

export const packRoutineCategoryToActivityType = (
  category: PackRoutineCategory
): string =>
  category === PACK_ROUTINE_CATEGORY.BREAK
    ? ACTIVITY_SEQUENCE_TYPE.BREAKING
    : category;

export const collectPackActivityMatchIds = (
  activities?: ActivityType[]
): Set<string> => {
  const ids = new Set<string>();
  activities?.forEach((activity) => {
    if (activity.id) {
      ids.add(activity.id);
    }
    if (activity.activity_template_id) {
      ids.add(activity.activity_template_id);
    }
  });
  return ids;
};

export const mergeRoutineActivityMatchIds = (
  ...activityLists: (ActivityType[] | undefined)[]
): Set<string> => {
  const ids = new Set<string>();
  activityLists.forEach((activities) => {
    collectPackActivityMatchIds(activities).forEach((id) => ids.add(id));
  });
  return ids;
};

export const getActivitiesForUnlockTarget = (
  settings: SettingsType,
  { type, customRoutinePosition }: UnlockHabitPackTarget
): ActivityType[] | undefined => {
  if (isNumber(customRoutinePosition)) {
    return settings.custom_routines?.[customRoutinePosition]
      ?.standalone_activities;
  }

  if (type === ACTIVITY_TYPES.MORNING) {
    return settings.morning_activities;
  }
  if (type === ACTIVITY_TYPES.EVENING) {
    return settings.evening_activities;
  }
  if (type === ACTIVITY_TYPES.BREAKING) {
    return settings.break_activities;
  }
  return settings.standalone_activities;
};

type HabitPackChoiceType = ChoiceType & {
  activity_template_id?: string;
};

type HabitPackActivityType = ActivityType & {
  pack_id?: string | null;
  tags?: string[];
};

const remapLogQuantityQuestions = (questions?: LogQuantityQuestion[]) =>
  questions?.map((question) => ({
    ...question,
    id: uuid()
  }));

const remapChoices = (choices?: ChoiceType[]) =>
  choices?.map((choice) => {
    const typedChoice = choice as HabitPackChoiceType;
    return {
      ...choice,
      id: uuid(),
      activity_template_id: typedChoice.activity_template_id ?? typedChoice.id,
      log_quantity_questions: remapLogQuantityQuestions(
        choice.log_quantity_questions
      )
    };
  });

export const createRoutineActivityFromHabitPack = (
  activity: ActivityType
): ActivityType => {
  const normalized = normalizeHabitPackActivityForRoutine(activity);
  const {
    activity_type: _activityType,
    pack_id: _packId,
    ...rest
  } = normalized as HabitPackActivityType;

  return {
    ...rest,
    id: uuid(),
    activity_template_id: activity.activity_template_id ?? activity.id,
    choices: remapChoices(activity.choices),
    log_quantity_questions: remapLogQuantityQuestions(
      activity.log_quantity_questions
    )
  };
};
